// ai-chat/src/lib/state/ai-chat.effects.ts
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, Action } from '@ngrx/store';
import { Observable, of, from, concat, firstValueFrom, timer, EMPTY } from 'rxjs';
import { catchError, map, switchMap, mergeMap, withLatestFrom, filter, take, tap, takeUntil } from 'rxjs/operators';
import { toObservable } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';

import { environment } from '@rapider/environments';

import { ProjectDataActions, ProjectDataService } from '@rapider/project-data';

import {
  ProjectChannelControllerService,
  ProjectChannelMessageControllerService,
  ProjectChannelMessageThreadControllerService,
  ProjectModelControllerService,
  AiFunctionCallControllerService,
  WorkflowManagementControllerService
} from '@rapider/sdk';

import { AuthStore } from '@rapider/authentication';

import { AiChatActions } from './ai-chat.actions';
import { selectActiveThreadId, selectChannelId, selectAiChatState, selectThreads, selectSelectedAiFunctionId, selectMessages } from './ai-chat.reducer';
import { WorkflowExecutionService } from '../services/workflow-execution.service';
import {
  AI_CHAT_CONFIG,
  ReasoningStep,
  AIChatMessage,
  ChatThread,
  generateTempId,
  buildFindDirectMessageChannelFilter,
  buildFindThreadsFilter,
  buildFindThreadMessagesFilter,
  buildCreateThreadPayload,
  buildDirectMessageFormDataPayload,
  mapApiThreadToChatThread,
  mapApiMessageToAiChatMessage,
  parseAiResponseText,
  parseVoiceTranscription,
  shouldFetchThreadMetadata,
  extractInteractionConfig,
  extractEntityData,
  extractAiActions,
  extractMemoryUpdatesFromResponse,
  extractAiActionsUIConfig,
  buildActionExecutionBackendPayload,
  buildDataOperationEventPayload,
  buildPathWithParams,
  buildExposedAiFunctionsFilter,
  extractWorkflowPlan,
  extractWorkflowPlanUpdate,
  executeJsonataTransformation,
  extractWorkflowStepData,
  extractToolCalls,
  extractAsyncProgress,
} from '../ai-chat.config';

/**
 * NgRx Effects handling asynchronous operations for the AI Chat.
 * Orchestrates direct messaging, thread pagination, and voice file transmission exclusively via the SDK.
 */
@Injectable()
export class AiChatEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly authStore = inject(AuthStore);

  // Inject the Project Data Service to offload dynamic endpoint executions
  private readonly projectDataService = inject(ProjectDataService);

  /**
   * Dedicated fetch stream reader to consume SSE (Server-Sent Events) from the backend.
   * Parses Activity frames into Reasoning steps, dispatching updates instantly to the NgRx store.
   */
  private sendStreamRequest(payload: any, tempAiMsgId: string): Observable<any> {
    return new Observable(observer => {
      // CRITICAL FIX: Target the api-key endpoint which natively supports multipart/form-data streams
      const url = `${environment.apiBaseUrl}/api-key/project-channel-messages/direct-messages/send`;

      const token = localStorage.getItem('Authorization') || localStorage.getItem('accessToken');

      // We DO NOT set 'Content-Type' manually. The browser will automatically set it to 
      // 'multipart/form-data' and append the correct WebKitFormBoundary for the files.
      const headers: any = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      // Reconstruct the FormData to safely carry File blobs and Voice recordings
      const formData = new FormData();
      const body = payload.body;
      Object.keys(body).forEach(key => {
        if (key === 'file' && Array.isArray(body[key])) {
          body[key].forEach((f: any) => formData.append('file', f));
        } else if (typeof body[key] === 'object') {
          formData.append(key, JSON.stringify(body[key]));
        } else {
          formData.append(key, String(body[key]));
        }
      });

      const controller = new AbortController();

      fetch(url, {
        method: 'POST',
        headers: headers,
        body: formData,
        signal: controller.signal
      }).then(async response => {
        if (!response.ok) {
          const errText = await response.text();
          observer.error(new Error(errText));
          return;
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';
        let finalResponse = null;

        if (!reader) {
          observer.error(new Error('No stream available'));
          return;
        }

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          // CRITICAL FIX: Robust regex handles both \n\n and \r\n\r\n chunk boundaries seamlessly
          let boundaryIndex = buffer.search(/\r?\n\r?\n/);
          while (boundaryIndex !== -1) {
            const match = buffer.match(/\r?\n\r?\n/)!;
            const chunk = buffer.slice(0, boundaryIndex);
            buffer = buffer.slice(boundaryIndex + match[0].length);

            let eventType = '';
            let data = '';

            for (const line of chunk.split(/\r?\n/)) {
              if (line.startsWith('event:')) eventType = line.slice(6).trim();
              else if (line.startsWith('data:')) data += (data ? '\n' : '') + line.slice(5).trim();
            }

            if (eventType && data) {
              try {
                const parsed = JSON.parse(data);
                if (eventType === 'activity') {
                  const frame = parsed;

                  // Map specific backend statuses appropriately
                  let stepStatus: 'pending' | 'active' | 'completed' | 'failed' = 'active';
                  if (frame.status === 'success') stepStatus = 'completed';
                  else if (frame.status === 'error') stepStatus = 'failed';
                  else if (frame.status === 'pending') stepStatus = 'pending';

                  // Extract correct payload fields based on ActivityStream schema matching Mobile reference
                  const rawSubtitle = frame.subtitle || frame.notebook?.message;
                  const isNoActiveState = (str?: string): boolean =>
                    Boolean(str && str.toLowerCase().includes('no active'));
                  const cleanSubtitle = rawSubtitle && !isNoActiveState(rawSubtitle)
                    ? rawSubtitle
                    : (frame.notebook?.summary && !isNoActiveState(frame.notebook.summary)
                        ? frame.notebook.summary
                        : undefined);

                  const step: ReasoningStep = {
                    id: frame.sequence?.toString() || frame.id || frame.eventType || String(Date.now()),
                    title: frame.title || frame.eventType || 'Thinking...',
                    subtitle: cleanSubtitle,
                    content: cleanSubtitle || '',
                    status: stepStatus,
                    capabilityName: frame.capabilityName,
                    correlationId: frame.correlationId,
                    eventType: frame.eventType,
                    data: frame.data || frame.payload || (frame.currentStep ? frame.currentStep : undefined),
                    payload: frame.data || frame.payload,
                  };
                  this.store.dispatch(AiChatActions.updateReasoningSteps({ messageId: tempAiMsgId, step }));
                } else if (eventType === 'tool_call') {
                  this.store.dispatch(AiChatActions.updateToolCalls({ messageId: tempAiMsgId, toolCalls: parsed.toolCalls || [parsed] }));
                } else if (eventType === 'progress') {
                  this.store.dispatch(AiChatActions.updateAsyncProgress({ messageId: tempAiMsgId, progress: parsed.progress || parsed }));
                } else if (eventType === 'final_response') {
                  finalResponse = parsed;
                } else if (eventType === 'done') {
                  // CRITICAL FIX: Explicitly close the stream when the backend emits the 'done' marker
                  if (finalResponse) {
                    observer.next(finalResponse);
                  } else {
                    observer.next({ message: { text: '' } });
                  }
                  observer.complete();
                  return;
                } else if (eventType === 'runtime_error') {
                  observer.error(new Error(parsed.error || parsed.message || 'Runtime error in stream'));
                  return;
                }
              } catch (e) {
                console.warn('Failed to parse stream chunk', data);
              }
            }
            // Check if there are any remaining chunks in the buffer
            boundaryIndex = buffer.search(/\r?\n\r?\n/);
          }
        }

        if (finalResponse) {
          observer.next(finalResponse);
        } else {
          // Fallback if the connection closes before 'done'
          observer.error(new Error('Stream closed without final response'));
        }
        observer.complete();
      }).catch(err => {
        observer.error(err);
      });

      return () => controller.abort();
    });
  }

  // Auto-generated SDK Controllers
  private readonly channelController = inject(ProjectChannelControllerService);
  private readonly projectChannelMessageController = inject(ProjectChannelMessageControllerService);
  private readonly threadController = inject(ProjectChannelMessageThreadControllerService);
  private readonly messageController = inject(ProjectChannelMessageControllerService);
  private readonly projectModelController = inject(ProjectModelControllerService);
  private readonly aiFunctionController = inject(AiFunctionCallControllerService);
  private readonly http = inject(HttpClient);
  private readonly workflowExecutionService = inject(WorkflowExecutionService);
  private readonly workflowManagementController = inject(WorkflowManagementControllerService);

  private readonly user$ = toObservable(this.authStore.user);

  /**
   * Announces a Company Knowledge change to the rest of the app when a reply reports notes the
   * runtime saved during the turn (`memoryUpdates`). Open Knowledge views listen for the window
   * event and reload, so a saved rule appears there without a manual refresh.
   */
  announceKnowledgeChange$ = createEffect(() => this.actions$.pipe(
    ofType(AiChatActions.sendMessageSuccess),
    filter(({ aiMessage }) => (aiMessage.memoryUpdates?.length ?? 0) > 0),
    tap(() => window.dispatchEvent(new CustomEvent(AI_CHAT_CONFIG.constants.knowledgeChangedEvent))),
  ), { dispatch: false });

  /**
   * Orchestrates the complete 5-step Thread Initialization Flow on Application Load.
   * Uses `toObservable(user)` to wait until the userId is safely populated before triggering Step 1.
   * * Flow:
   * 1. Fetches DM channel for the user.
   * 2. Extracts existing DM channel ID.
   * 3. Creates new thread/channel if missing.
   * 4. Fetches and selects most recent thread.
   * 5. Populates the sidebar list.
   */
  initChat$ = createEffect(() => this.actions$.pipe(
    ofType(AiChatActions.initChat),
    switchMap(() => this.user$.pipe(
      // Ensure the user and an active project actually exist before proceeding
      filter(user => !!user?.id && !!this.authStore.activeProject()?.id),
      take(1),
      switchMap(user => {
        const userId = user!.id;
        console.log('🤖 [AI Chat Init] Step 0: Auth Verified. UserId =', userId);

        // Step 1: Get the direct channel
        const channelFilter = buildFindDirectMessageChannelFilter(userId, AI_CHAT_CONFIG.constants.aiReceiverId);
        console.log('🤖 [AI Chat Init] Step 1: Finding direct channel with filter:', channelFilter);

        return this.channelController.find({ filter: channelFilter as any }).pipe(
          switchMap((channels: any[]) => {

            if (!channels || channels.length === 0) {
              // Step 3: No directMessageChannelId -> Generate a new thread & channel via SDK
              console.log('🤖 [AI Chat Init] Step 3: No DM channel found. Generating new thread and channel automatically.');

              // Emitting channelId as explicitly '' lets the backend auto-generate the channel
              const payload = buildCreateThreadPayload(AI_CHAT_CONFIG.constants.defaultThreadTitle, '', '');

              return this.threadController.create(payload).pipe(
                switchMap((newApiThread: any) => {
                  const mappedThread = mapApiThreadToChatThread(newApiThread);
                  console.log('🤖 [AI Chat Init] Success: Thread & Channel created!', mappedThread);

                  // Step 2 & 5 (Implied): Save state and populate sidebar
                  // Explicitly type the array to prevent strict-type TS errors when pushing differing actions
                  const actions: any[] = [
                    AiChatActions.initChatSuccess({ channelId: mappedThread.channelId || '', threads: [mappedThread] }),
                    AiChatActions.selectThread({ threadId: mappedThread.id })
                  ];
                  return from(actions);
                })
              );
            } else {
              // Step 2 & 4: Channel exists -> Get the threads for this channel via SDK
              const channelId = channels[0].id;
              console.log('🤖 [AI Chat Init] Step 2: DM Channel found. ChannelId =', channelId);

              const threadFilter = buildFindThreadsFilter(channelId, AI_CHAT_CONFIG.constants.threadsPageSize, 0);
              console.log('🤖 [AI Chat Init] Step 4: Finding threads with filter:', threadFilter);

              return this.threadController.find({ filter: threadFilter as any }).pipe(
                switchMap((apiThreads: any[]) => {
                  const threads = apiThreads.map(mapApiThreadToChatThread);
                  console.log(`🤖 [AI Chat Init] Success: Loaded ${threads.length} existing threads.`);

                  // Save threads to state (Step 5)
                  const actions: any[] = [AiChatActions.initChatSuccess({ channelId, threads })];

                  if (threads.length > 0) {
                    // Choose the most recent thread to show chat history
                    console.log(`🤖 [AI Chat Init] Step 4/5: Selecting most recent thread: ${threads[0].id}`);
                    actions.push(AiChatActions.selectThread({ threadId: threads[0].id }));
                  } else {
                    console.log(`🤖 [AI Chat Init] Fallback: Channel exists but has 0 threads. Initiating createNewThread.`);
                    actions.push(AiChatActions.createNewThread());
                  }

                  return from(actions);
                })
              );
            }
          }),
          catchError((error: any) => {
            console.error('🤖 [AI Chat Init] Fatal Error:', error);
            return of(AiChatActions.initChatFailure({ error: error.message || 'Initialization failed' }));
          })
        );
      })
    ))
  ));

  /**
   * Fetches specific project models lazily to retrieve layout formatting metadata securely without redundantly hitting endpoints.
   * Now securely uses the ProjectDataService wrapper to cache retrieved models globally and avoid duplicate requests.
   */
  loadProjectModel$ = createEffect(() => this.actions$.pipe(
    ofType(AiChatActions.loadProjectModel),
    mergeMap(({ entityName }) => {
      return this.projectDataService.getProjectModelWithoutRelations(entityName).pipe(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        map((projectModel: any[]) => AiChatActions.loadProjectModelSuccess({ entityName, projectModel })),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        catchError((error: any) => of(AiChatActions.loadProjectModelFailure({ entityName, error: error.message })))
      );
    })
  ));

  /**
   * Fetches dynamically configured AI functions exposed as features to be listed in the chat dropdown.
   */
  loadAvailableAiFunctions$ = createEffect(() => this.actions$.pipe(
    ofType(AiChatActions.loadAvailableAiFunctions),
    switchMap(() => {
      // Guard: Do not query functions if no active workspace is selected
      if (!this.authStore.activeProject()) {
        return EMPTY;
      }
      const filter = buildExposedAiFunctionsFilter();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return this.aiFunctionController.find({ filter: filter as any, mode: 'project-and-public-functions' }).pipe(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        map((functions: any[]) => AiChatActions.loadAvailableAiFunctionsSuccess({ functions })),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        catchError((error: any) => of(AiChatActions.loadAvailableAiFunctionsFailure({ error: error.message })))
      );
    })
  ));

  /**
   * Appends additional threads to the sidebar pagination (Load More logic) via the SDK.
   */
  loadMoreThreads$ = createEffect(() => this.actions$.pipe(
    ofType(AiChatActions.loadMoreThreads),
    withLatestFrom(this.store.select(selectAiChatState)),
    switchMap(([_, state]) => {
      if (!state.channelId) return of(AiChatActions.loadMoreThreadsFailure({ error: 'No active channel' }));

      console.log('🤖 [AI Chat Pagination] Loading more threads at offset:', state.threadsOffset);
      const threadFilter = buildFindThreadsFilter(state.channelId, AI_CHAT_CONFIG.constants.threadsPageSize, state.threadsOffset);

      return this.threadController.find({ filter: threadFilter as any }).pipe(
        map((apiThreads: any[]) => AiChatActions.loadMoreThreadsSuccess({ threads: apiThreads.map(mapApiThreadToChatThread) })),
        catchError((error: any) => of(AiChatActions.loadMoreThreadsFailure({ error: error.message })))
      );
    })
  ));

  /**
   * Fetches messages for a specific Thread when clicked in the sidebar via the SDK.
   */
  selectThread$ = createEffect(() => this.actions$.pipe(
    ofType(AiChatActions.selectThread),
    switchMap(({ threadId }) => {
      console.log('🤖 [AI Chat Thread] Loading messages for thread:', threadId);
      const filter = buildFindThreadMessagesFilter(threadId);

      return this.messageController.findDirectMessages({ filter: filter as any }).pipe(
        map((apiMessages: any[]) => AiChatActions.selectThreadSuccess({ messages: apiMessages.map(mapApiMessageToAiChatMessage) })),
        catchError((error: any) => of(AiChatActions.sendMessageFailure({ error: error.message })))
      );
    })
  ));

  /**
   * Explicitly generates a new Thread and Channel ID when the user hits "New Chat" via the SDK.
   */
  createNewThread$ = createEffect(() => this.actions$.pipe(
    ofType(AiChatActions.createNewThread),
    withLatestFrom(this.store.select(selectChannelId)),
    map(([_, channelId]) => {
      console.log('🤖 [AI Chat Thread] Creating new temporary thread locally');
      const thread: ChatThread = {
        id: 'temp-thread',
        title: AI_CHAT_CONFIG.constants.defaultThreadTitle,
        channelId: channelId || '',
        createdDate: new Date(),
      };
      return AiChatActions.createNewThreadSuccess({ thread });
    })
  ));

  /**
   * Intercepts an AI Chat Trigger dispatched natively from the dashboard module.
   * Generates a new thread sequentially and automatically commits the mapped trigger prompt.
   */
  executeAiChatTrigger$ = createEffect(() => this.actions$.pipe(
    ofType(AiChatActions.executeAiChatTrigger),
    withLatestFrom(this.store.select(selectChannelId)),
    switchMap(([{ prompt }, channelId]) => {
      const payload = buildCreateThreadPayload(AI_CHAT_CONFIG.constants.defaultThreadTitle, prompt, channelId || '');
      return this.threadController.create(payload).pipe(
        switchMap((apiThread: any) => {
          const thread = mapApiThreadToChatThread(apiThread);
          return from([
            AiChatActions.createNewThreadSuccess({ thread }),
            AiChatActions.sendMessage({ text: prompt })
          ]);
        }),
        catchError((error: any) => of(AiChatActions.sendMessageFailure({ error: error.message })))
      );
    })
  ));

  /**
   * Fetches full thread details (all fields) when entering a specific thread.
   */
  loadThreadDetailsOnSelect$ = createEffect(() => this.actions$.pipe(
    ofType(AiChatActions.selectThread),
    filter(({ threadId }) => !!threadId && threadId !== 'temp-thread'),
    map(({ threadId }) => AiChatActions.fetchThread({ threadId }))
  ));

  /**
   * Fetches specific thread metadata explicitly and syncs it into state.
   */
  fetchThread$ = createEffect(() => this.actions$.pipe(
    ofType(AiChatActions.fetchThread),
    switchMap(({ threadId }) => {
      return this.threadController.findById({ id: threadId }).pipe(
        map((apiThread: any) => AiChatActions.fetchThreadSuccess({ thread: mapApiThreadToChatThread(apiThread) })),
        catchError((error: any) => of(AiChatActions.fetchThreadFailure({ error: error.message })))
      );
    })
  ));

  sendMessage$ = createEffect(() => this.actions$.pipe(
    ofType(AiChatActions.sendMessage),
    withLatestFrom(
      this.store.select(selectActiveThreadId),
      this.store.select(selectChannelId),
      this.store.select(selectThreads),
      this.store.select(selectSelectedAiFunctionId)
    ),
    switchMap(([actionPayload, activeThreadId, channelId, threads, selectedAiFunctionId]) => {
      const { text, attachments, data } = actionPayload;
      // FIX: Use explicit messageType, fallback to 'user-message' instead of NgRx action type
      const type = actionPayload.messageType || 'user-message';
      const isConfirmation = (actionPayload as any).isConfirmation;
      const isShownInChat = (actionPayload as any).isShownInChat !== false && (actionPayload as any).isHidden !== true;
      const isHidden = !isShownInChat;

      console.log('  [AI Chat Send] Dispatching text message...', { isShownInChat });
      const tempMsgId = generateTempId();
      const tempAiMsgId = generateTempId();

      const optimisticMessage = { id: tempMsgId, text, isUser: true, timestamp: new Date(), attachments, type, isConfirmation, isShownInChat, isHidden };
      const optimisticAiMessage = { id: tempAiMsgId, text: '', isUser: false, timestamp: new Date(), type: 'ai-message', reasoningSteps: [], isStreaming: true };

      // Immediate UI update stream (Show AI Bubble instantly if not a background confirmation or hidden action)
      const optimisticUpdates$ = isConfirmation
        ? of(
            AiChatActions.addMessage({ message: optimisticMessage }),
            AiChatActions.addMessage({ message: optimisticAiMessage as AIChatMessage }),
            AiChatActions.setAiThinking({ isThinking: true })
          )
        : isHidden
        ? of(
            AiChatActions.addMessage({ message: optimisticMessage }),
            AiChatActions.addMessage({ message: optimisticAiMessage as AIChatMessage }),
            AiChatActions.setAiThinking({ isThinking: true })
          )
        : of(
            AiChatActions.addMessage({ message: optimisticMessage }),
            AiChatActions.addMessage({ message: optimisticAiMessage as AIChatMessage }),
            AiChatActions.setAiThinking({ isThinking: true })
          );

      // Resolve thread ID logic
      const threadReady$ = (activeThreadId && activeThreadId !== 'temp-thread')
        ? of(activeThreadId)
        : this.threadController.create(buildCreateThreadPayload(AI_CHAT_CONFIG.constants.defaultThreadTitle, text, channelId || '')).pipe(
          tap((apiThread: any) => {
            const thread = mapApiThreadToChatThread(apiThread);
            this.store.dispatch(AiChatActions.createNewThreadSuccess({ thread }));
          }),
          map((res: any) => res.id!)
        );

      // Async network transmission stream
      const apiCalls$ = threadReady$.pipe(
        switchMap((threadId: string) => {
          if (threadId !== activeThreadId) {
            this.store.dispatch(AiChatActions.setActiveThread({ threadId }));
          }

          const hasFiles = attachments && attachments.length > 0;
          console.log(`  [AI Chat Send] Sending payload via SDK with threadId ${threadId}...`);

          let apiRequest$;

          if (isConfirmation) {
            // CRITICAL FIX: Send natively as 'action-response' so the database strictly reflects the new 3-type architecture.
            // We use the SDK here because confirmation messages don't require streaming
            apiRequest$ = this.projectChannelMessageController.sendDirectMessage({
              body: {
                message: text,
                type: 'action-response',
                threadId: threadId,
                status: 'sent',
                data: { isConfirmation: true, ...(data || {}) }
              } as any
            });
          } else {
            const functionIdToUse = isConfirmation ? undefined : (actionPayload.aiFunctionIdOverride || selectedAiFunctionId);

            // Use the appropriate SDK method depending on attachment presence, inject the dynamically selected AI Function ID
            const rawPayload = buildDirectMessageFormDataPayload(text, threadId, AI_CHAT_CONFIG.constants.aiReceiverId, hasFiles ? attachments : [], [], functionIdToUse, type);

            // Inject explicit data payload (e.g. selected interaction items) natively into the request
            if (data || isHidden) {
              rawPayload.body.data = rawPayload.body.data || {};
              if (data) {
                Object.assign(rawPayload.body.data, data);
              }
              if (isHidden) {
                rawPayload.body.data.isShownInChat = false;
                rawPayload.body.data.isHidden = true;
              }
            }

            apiRequest$ = this.sendStreamRequest(rawPayload, tempAiMsgId);
          }

          return apiRequest$.pipe(
            switchMap((response: any) => {
              console.log('📥 [AI Chat Receive] Successfully received response:', response);

              // CRITICAL FIX: Distinguish between user and AI message IDs to correctly set originatingRequestMessageId
              const isUserResponse = response.senderId && response.senderId !== AI_CHAT_CONFIG.constants.aiReceiverId && response.type === 'user-message';
              const aiMsgId = response.aiMessage?.id || (typeof response.message === 'object' ? response.message?.id : undefined) || (!isUserResponse ? response.id : undefined);
              const userMsgId = response.userMessage?.id || (isUserResponse ? response.id : undefined);

              const realId = aiMsgId;

              // EXTRACT DATA AND ACTIONS SECURELY
              const aiText = parseAiResponseText(response);
              const returnedThreadId = response.message?.threadId || (response as any).userMessage?.threadId || response.threadId;

              const entityData = extractEntityData(response);
              const actions = extractAiActions(response);
              const actionsUIConfig = extractAiActionsUIConfig(response);
              const workflowPlan = extractWorkflowPlan(response);
              const workflowPlanUpdate = extractWorkflowPlanUpdate(response);
              const transcription = parseVoiceTranscription(response);
              const interactionConfig = extractInteractionConfig(response);
              const toolCalls = extractToolCalls(response);
              const asyncProgress = extractAsyncProgress(response);
              const memoryUpdates = extractMemoryUpdatesFromResponse(response);

              const actionsToDispatch: any[] = [];

              if (!activeThreadId && returnedThreadId) {
                actionsToDispatch.push(AiChatActions.setActiveThread({ threadId: returnedThreadId }));
              }
              // User message ID update
              if (userMsgId) {
                actionsToDispatch.push(AiChatActions.updateMessageId({ tempId: tempMsgId, realId: userMsgId }));
              }
              if (transcription && (userMsgId || realId)) {
                actionsToDispatch.push(AiChatActions.updateTranscription({ messageId: userMsgId || realId, text: transcription }));
              }

              // FIX: Ensure message is pushed if ANY valid payload exists!
              if (aiText || entityData || (actions && actions.length > 0) || workflowPlan || workflowPlanUpdate || interactionConfig || toolCalls || asyncProgress) {
                actionsToDispatch.push(AiChatActions.sendMessageSuccess({
                  aiMessage: {
                    id: aiMsgId || generateTempId(), // CRITICAL FIX: Use real DB ID
                    originatingRequestMessageId: userMsgId,
                    type: workflowPlanUpdate ? 'action-response' : 'ai-message', // CRITICAL FIX: Enforce core message types natively
                    text: aiText || '',
                    isUser: false,
                    timestamp: new Date(),
                    entityData,
                    actions,
                    actionsUIConfig,
                    workflowPlan,
                    workflowPlanUpdate,
                    interactionConfig,
                    toolCalls,
                    asyncProgress,
                    memoryUpdates
                  },
                  tempAiMsgId: tempAiMsgId
                }));
              } else {
                // If no payload comes back, silently clear the temp message by dispatching success with an empty text bubble.
                actionsToDispatch.push(AiChatActions.sendMessageSuccess({
                  aiMessage: {
                    id: aiMsgId || generateTempId(),
                    type: 'ai-message',
                    text: 'Task completed.',
                    isUser: false,
                    timestamp: new Date()
                  },
                  tempAiMsgId: tempAiMsgId
                }));
                actionsToDispatch.push(AiChatActions.setAiThinking({ isThinking: false }));
              }

              // Validate if the active thread currently has an empty/default title
              const activeThread = threads.find(t => t.id === (activeThreadId || returnedThreadId));
              if (shouldFetchThreadMetadata(activeThread?.title, AI_CHAT_CONFIG.constants.defaultThreadTitle)) {
                actionsToDispatch.push(AiChatActions.fetchThread({ threadId: returnedThreadId || activeThreadId! }));
              }

              return from(actionsToDispatch);
            })
          );
        }),
        catchError((error: any) => {
          console.error('❌ [AI Chat Send] Network transmission failed:', error);
          const errorText = error?.error?.message || error?.message || AI_CHAT_CONFIG.texts.defaultErrorMessage;
          return of(
            AiChatActions.setAiThinking({ isThinking: false }),
            AiChatActions.addMessage({ message: { id: generateTempId(), text: errorText, isUser: false, timestamp: new Date(), isError: true } }),
            AiChatActions.sendMessageFailure({ error: errorText })
          );
        })
      );

      // Ensures optimistic UI resolves fully before firing async tasks
      return concat(optimisticUpdates$, apiCalls$);
    })
  ));

  /**
     * Transmits recorded voice blobs natively via the MessageController SDK.
     */
  sendVoiceMessage$ = createEffect(() => this.actions$.pipe(
    ofType(AiChatActions.sendVoiceMessage),
    withLatestFrom(
      this.store.select(selectActiveThreadId),
      this.store.select(selectChannelId),
      this.store.select(selectThreads),
      this.store.select(selectSelectedAiFunctionId)
    ),
    switchMap(([{ file }, activeThreadId, channelId, threads, selectedAiFunctionId]) => {

      console.log('🎤 [AI Chat Voice] Dispatching voice message...');
      const tempMsgId = generateTempId();
      const tempAiMsgId = generateTempId();
      const objectUrl = URL.createObjectURL(file);
      const optimisticMessage = { id: tempMsgId, text: '', isUser: true, timestamp: new Date(), isVoiceMessage: true, voiceUrl: objectUrl };
      const optimisticAiMessage = { id: tempAiMsgId, text: '', isUser: false, timestamp: new Date(), type: 'ai-message', reasoningSteps: [], isStreaming: true };

      const optimisticUpdates$ = of(
        AiChatActions.addMessage({ message: optimisticMessage }),
        AiChatActions.addMessage({ message: optimisticAiMessage as AIChatMessage }),
        AiChatActions.setAiThinking({ isThinking: true })
      );

      const threadReady$ = activeThreadId
        ? of(activeThreadId)
        : this.threadController.create(buildCreateThreadPayload(AI_CHAT_CONFIG.constants.defaultThreadTitle, 'Voice Message', channelId || '')).pipe(
          map((res: any) => res.id!)
        );

      const apiCalls$ = threadReady$.pipe(
        switchMap((threadId: string) => {

          if (threadId !== activeThreadId) {
            this.store.dispatch(AiChatActions.setActiveThread({ threadId }));
          }

          // Inject the dynamically selected AI Function ID into the voice payload
          const payload = buildDirectMessageFormDataPayload('', threadId, AI_CHAT_CONFIG.constants.aiReceiverId, [], [file], selectedAiFunctionId);

          return this.sendStreamRequest(payload, tempAiMsgId).pipe(
            switchMap((response: any) => {
              console.log('📥 [AI Chat Receive] Voice message successfully processed by backend.');

              // CRITICAL FIX: Distinguish between user and AI message IDs to correctly set originatingRequestMessageId
              const isUserResponse = response.senderId && response.senderId !== AI_CHAT_CONFIG.constants.aiReceiverId && response.type === 'user-message';
              const aiMsgId = response.aiMessage?.id || (!isUserResponse && response.type === 'ai-message' ? response.id : undefined);
              const userMsgId = response.message?.id || response.userMessage?.id || (isUserResponse ? response.id : undefined);

              const realId = aiMsgId;
              const aiText = parseAiResponseText(response);
              const transcription = parseVoiceTranscription(response);
              const returnedThreadId = response.message?.threadId || (response as any).userMessage?.threadId || response.threadId;

              // EXTRACT DATA AND ACTIONS SECURELY
              const entityData = extractEntityData(response);
              const actions = extractAiActions(response);
              const actionsUIConfig = extractAiActionsUIConfig(response);
              const workflowPlan = extractWorkflowPlan(response);
              const workflowPlanUpdate = extractWorkflowPlanUpdate(response);
              const interactionConfig = extractInteractionConfig(response);
              const toolCalls = extractToolCalls(response);
              const asyncProgress = extractAsyncProgress(response);
              const memoryUpdates = extractMemoryUpdatesFromResponse(response);

              const actionsToDispatch: any[] = [];

              if (!activeThreadId && returnedThreadId) {
                actionsToDispatch.push(AiChatActions.setActiveThread({ threadId: returnedThreadId }));
              }
              if (userMsgId) {
                actionsToDispatch.push(AiChatActions.updateMessageId({ tempId: tempMsgId, realId: userMsgId }));
              }
              if (transcription) {
                actionsToDispatch.push(AiChatActions.updateTranscription({ messageId: userMsgId || tempMsgId, text: transcription }));
              }

              // FIX: Ensure message is pushed if ANY valid payload exists!
              if (aiText || entityData || (actions && actions.length > 0) || workflowPlan || workflowPlanUpdate || interactionConfig || toolCalls || asyncProgress) {
                actionsToDispatch.push(AiChatActions.sendMessageSuccess({
                  aiMessage: {
                    id: aiMsgId || generateTempId(),
                    originatingRequestMessageId: userMsgId,
                    type: workflowPlanUpdate ? 'action-response' : 'ai-message', // CRITICAL FIX: Enforce core message types natively
                    text: aiText || '',
                    isUser: false,
                    timestamp: new Date(),
                    entityData,
                    actions,
                    actionsUIConfig,
                    workflowPlan,
                    workflowPlanUpdate,
                    interactionConfig,
                    toolCalls,
                    asyncProgress,
                    memoryUpdates
                  },
                  tempAiMsgId
                }));
              } else {
                actionsToDispatch.push(AiChatActions.sendMessageSuccess({
                  aiMessage: {
                    id: aiMsgId || generateTempId(),
                    type: 'ai-message',
                    text: 'Audio processed.',
                    isUser: false,
                    timestamp: new Date()
                  },
                  tempAiMsgId
                }));
                actionsToDispatch.push(AiChatActions.setAiThinking({ isThinking: false }));
              }

              // Validate if the active thread currently has an empty/default title
              const activeThread = threads.find(t => t.id === (activeThreadId || returnedThreadId));
              if (shouldFetchThreadMetadata(activeThread?.title, AI_CHAT_CONFIG.constants.defaultThreadTitle)) {
                actionsToDispatch.push(AiChatActions.fetchThread({ threadId: returnedThreadId || activeThreadId! }));
              }

              return from(actionsToDispatch);
            })
          );
        }),
        catchError((error: any) => {
          console.error('❌ [AI Chat Voice] Network transmission failed:', error);
          const errorText = error?.error?.message || error?.message || AI_CHAT_CONFIG.texts.defaultErrorMessage;
          return of(
            AiChatActions.setAiThinking({ isThinking: false }),
            AiChatActions.addMessage({ message: { id: generateTempId(), text: errorText, isUser: false, timestamp: new Date(), isError: true } }),
            AiChatActions.sendMessageFailure({ error: errorText })
          );
        })
      );

      return concat(optimisticUpdates$, apiCalls$);
    })
  ));

  /**
   * Intercepts endpoint actions in bulk or individually.
   * Offloads execution securely to the WorkflowExecutionService to handle sequential runs and Handlebars state parsing.
   */
  executeEndpointActions$ = createEffect(() => this.actions$.pipe(
    ofType(AiChatActions.executeEndpointActions),
    withLatestFrom(
      this.store.select(selectActiveThreadId)
    ),
    tap(([{ actions, messageId, originatingRequestMessageId }, activeThreadId]) => {
      // Fire and forget via the service. The service dispatches state updates directly to the store natively.
      this.workflowExecutionService.executeActionsSequentially(actions, messageId, activeThreadId, 0, undefined, originatingRequestMessageId);
    })
  ), { dispatch: false });

  /**
   * Orchestrates the execution of a multi-step Workflow Plan.
   * Hands the structured declarative payload back to the Workflow Execution Service to iteratively resolve and share context variables.
   */
  executeWorkflowPlan$ = createEffect(() => this.actions$.pipe(
    ofType(AiChatActions.executeWorkflowPlan),
    withLatestFrom(this.store.select(selectActiveThreadId)),
    tap(([{ plan, messageId }, activeThreadId]) => {
      // Fire and forget via the service. State will map to the DOM organically.
      this.workflowExecutionService.executeWorkflowPlanSequentially(plan, messageId, activeThreadId);
    })
  ), { dispatch: false });

  /**
   * Translates a user's selection from a structured Interaction Widget into a standard text message.
   * Submits the selection back to the active thread for the AI to digest without breaking the natural flow.
   */
  sendInteractionResponse$ = createEffect(() => this.actions$.pipe(
    ofType(AiChatActions.sendInteractionResponse),
    map(({ selectedOptions }) => {
      const feedbackText = selectedOptions.length > 0
        ? selectedOptions.map(o => o.title).join(', ')
        : 'No options were selected.';

      const interactionData: Record<string, any> = {};

      // Turn-input contract (same as mobile): the message carries the titles for display, and
      // `data.selection.value` carries the chosen option value(s) the server resolves the answer
      // from. The server never read the former `selectedEntityItem(s)` keys, so it only saw the
      // title — two options titled the same could not be told apart and the question came back.
      if (selectedOptions.length === 1) {
        interactionData['selection'] = { value: selectedOptions[0].value };
      } else if (selectedOptions.length > 1) {
        interactionData['selection'] = { value: selectedOptions.map(o => o.value) };
      }

      return AiChatActions.sendMessage({
        text: feedbackText,
        messageType: 'user-message',
        isConfirmation: false,
        data: interactionData
      });
    })
  ));

  /**
   * Translates an explicit skip action from the user on an Interaction Widget into a standard chat message.
   */
  skipInteraction$ = createEffect(() => this.actions$.pipe(
    ofType(AiChatActions.skipInteraction),
    map(() => AiChatActions.sendMessage({
      text: 'Skipped interaction.',
      messageType: 'user-message',
      isConfirmation: false
    }))
  ));


  /**
   * Manually executes a JSONata transformation when the user clicks the Execute button.
   * Pulls the context from the local data store (or mock data) and evaluates the expression.
   */
  executeJsonata$ = createEffect(() => this.actions$.pipe(
    ofType(AiChatActions.executeJsonata),
    withLatestFrom(
      this.store.select(selectMessages)
    ),
    switchMap(async ([{ messageId, expression }, messages]) => {
      try {
        // Build an enriched context that supports both lowercase and TitleCase entity names 
        // to gracefully handle variations in AI-generated JSONata syntax
        const enrichedContextData: any = {};
        messages.forEach(msg => {
          if (msg.entityData && msg.entityData.entityName) {
            const rawData = msg.entityData.data;
            const name = msg.entityData.entityName;
            enrichedContextData[name.toLowerCase()] = rawData;
            enrichedContextData[name.charAt(0).toUpperCase() + name.slice(1)] = rawData;
          }
        });

        // Execute the pure JSONata evaluator function
        const result = await executeJsonataTransformation(expression, enrichedContextData);

        // Build the simulated response message natively avoiding empty/null values
        const resultMessage: any = {
          id: generateTempId(),
          text: result !== undefined ? 'Transformation executed successfully.' : 'Transformation yielded no results.',
          isUser: false,
          timestamp: new Date(),
          type: 'action-response', // Simplified type enforcement
          transformationResult: result || { message: "No data returned from transformation." }
        };

        return AiChatActions.sendMessageSuccess({ aiMessage: resultMessage });

      } catch (error: any) {
        console.error('JSONata Execution Failed:', error);
        return AiChatActions.sendMessageFailure({ error: `JSONata Error: ${error.message}` });
      }
    })
  ));

  /**
   * Automatically triggers the workflow polling engine if an executed action returns a correlationId in its state.
   * This bridges the gap between endpoint actions and the AsyncProgress UI card.
   */
  triggerWorkflowPollingOnAction$ = createEffect(() => this.actions$.pipe(
    ofType(AiChatActions.setActionExecutionState),
    filter(({ executionState }) => !!executionState.correlationId),
    map(({ messageId, executionState }) => AiChatActions.startWorkflowPolling({
      messageId,
      correlationId: executionState.correlationId!
    }))
  ));

  /**
   * Automatically triggers the workflow polling engine if a retrieved or streamed message contains a correlationId.
   * This catches backend-initiated async workflows (e.g. metadata.correlationId) natively.
   */
  triggerWorkflowPollingOnMessage$ = createEffect(() => this.actions$.pipe(
    ofType(AiChatActions.sendMessageSuccess),
    filter(({ aiMessage }) => !!aiMessage.asyncProgress?.correlationId && aiMessage.asyncProgress?.isPolling === true),
    map(({ aiMessage }) => AiChatActions.startWorkflowPolling({
      messageId: aiMessage.id,
      correlationId: aiMessage.asyncProgress!.correlationId!
    }))
  ));

  /**
     * The Heavy Lifter: Polls the backend for workflow logs every 1.5 seconds.
     * Maps the backend 'steps' and 'executionLogs' into the AsyncProgress UI Card dynamically.
     */
  pollWorkflowLogs$ = createEffect(() => this.actions$.pipe(
    ofType(AiChatActions.startWorkflowPolling),
    withLatestFrom(this.store.select(selectActiveThreadId)),
    mergeMap(([{ messageId, correlationId }, activeThreadId]) => {
      const processedExecutionIds = new Set<string>();

      // Execute the loop every 1.5s until 'Stop Workflow Polling' is dispatched
      return timer(0, 1500).pipe(
        switchMap(() => {
          // CRITICAL FIX: Use the SDK's JWT-authenticated method instead of raw HttpClient
          return this.workflowManagementController.getWorkflowLogsWithJWT({ correlationId });
        }),
        map((response: any) => {
          const logs = response.executionLogs || [];
          const steps = response.steps || [];

          // Map backend logs to the UI's AsyncProgressStep schema
          const mappedSteps = steps.map((step: any, index: number) => {
            const log = logs.find((l: any) => l.stepId === step.id || l.stepIndex === index);
            let status: 'pending' | 'active' | 'completed' | 'failed' = 'pending';
            if (log) {
              if (log.status === 'success' || log.status === 'completed') status = 'completed';
              else if (log.status === 'error' || log.status === 'failed') status = 'failed';
              else status = 'active';
            }
            return {
              title: step.description || step.name || `Task ${index + 1}`,
              status
            };
          });

          const total = steps.length || 1;
          const completed = mappedSteps.filter((s: any) => s.status === 'completed' || s.status === 'failed').length;
          const percentage = Math.round((completed / total) * 100);

          let resultMarkdown: string | undefined;
          if (percentage === 100 && logs.length > 0) {
            const lastLog = logs[logs.length - 1];
            if (lastLog?.response) {
              const resp = lastLog.response;
              if (typeof resp === 'string') {
                resultMarkdown = resp;
              } else {
                resultMarkdown =
                  resp.message || resp.text || resp.answer || resp.summary;
              }
            }
          }

          const workflowTitle = response.relatedWorkflowTriggerEvent?.title || response.workflow?.title || response.title || response.workflow?.name || response.name || 'Workflow';
          const progress = {
            percentage,
            statusText:
              percentage === 100
                ? `${workflowTitle} Complete`
                : `Executing ${workflowTitle}...`,
            steps: mappedSteps,
            isPolling: percentage < 100,
            resultMarkdown,
            correlationId,
          };

          const actionsToDispatch: Action[] = [
            AiChatActions.updateAsyncProgress({ messageId, progress })
          ];

          if (activeThreadId && logs.length > 0) {
            const newlyFoundMessageLogs = logs.filter(
              (log: any) => log.hasNewMessage && !processedExecutionIds.has(log.executionId)
            );

            if (newlyFoundMessageLogs.length > 0) {
              actionsToDispatch.push(AiChatActions.selectThread({ threadId: activeThreadId }));
              newlyFoundMessageLogs.forEach((log: any) => processedExecutionIds.add(log.executionId));
            }
          }

          // Determine if we should terminate the polling loop
          const conclusion = response.conclusion?.toLowerCase() || '';
          const isComplete = conclusion && conclusion !== 'pending' && conclusion !== 'in-progress' && conclusion !== 'processing';
          const allDone = steps.length > 0 && completed === steps.length;

          if (isComplete || allDone || percentage === 100) {
            if (activeThreadId) {
              actionsToDispatch.push(AiChatActions.selectThread({ threadId: activeThreadId }));
            }
            actionsToDispatch.push(AiChatActions.stopWorkflowPolling({ messageId }));
          }

          return from(actionsToDispatch);
        }),
        mergeMap(actions => actions), // flatten the array of dispatched actions
        takeUntil(this.actions$.pipe(
          ofType(AiChatActions.stopWorkflowPolling),
          filter(action => action.messageId === messageId)
        )),
        catchError(err => {
          console.error('Workflow Polling failed:', err);
          return of(AiChatActions.stopWorkflowPolling({ messageId }));
        })
      );
    })
  ));

  fetchWorkflowLogs$ = createEffect(() => this.actions$.pipe(
    ofType(AiChatActions.fetchWorkflowLogs),
    withLatestFrom(this.store.select(selectActiveThreadId)),
    mergeMap(([{ messageId, correlationId }, activeThreadId]) => {
      return this.workflowManagementController.getWorkflowLogsWithJWT({ correlationId }).pipe(
        map((response: any) => {
          const logs = response.executionLogs || [];
          const steps = response.steps || [];

          // Map backend logs to the UI's AsyncProgressStep schema
          const mappedSteps = steps.map((step: any, index: number) => {
            const log = logs.find((l: any) => l.stepId === step.id || l.stepIndex === index);
            let status: 'pending' | 'active' | 'completed' | 'failed' = 'pending';
            if (log) {
              if (log.status === 'success' || log.status === 'completed') status = 'completed';
              else if (log.status === 'error' || log.status === 'failed') status = 'failed';
              else status = 'active';
            }
            return {
              title: step.description || step.name || `Task ${index + 1}`,
              status
            };
          });

          const total = steps.length || 1;
          const completed = mappedSteps.filter((s: any) => s.status === 'completed' || s.status === 'failed').length;
          const percentage = Math.round((completed / total) * 100);

          let resultMarkdown: string | undefined;
          if (percentage === 100 && logs.length > 0) {
            const lastLog = logs[logs.length - 1];
            if (lastLog?.response) {
              const resp = lastLog.response;
              if (typeof resp === 'string') {
                resultMarkdown = resp;
              } else {
                resultMarkdown =
                  resp.message || resp.text || resp.answer || resp.summary;
              }
            }
          }

          const workflowTitle = response.workflow?.title || response.title || response.workflow?.name || response.name || 'Workflow';
          const progress = {
            percentage,
            statusText:
              percentage === 100
                ? `${workflowTitle} Complete`
                : `Executing ${workflowTitle}...`,
            steps: mappedSteps,
            isPolling: false,
            resultMarkdown,
            correlationId,
          };

          const actionsToDispatch: Action[] = [
            AiChatActions.updateAsyncProgress({ messageId, progress })
          ];

          if (activeThreadId && logs.length > 0) {
            const hasNewMessage = logs.some((log: any) => log.hasNewMessage);
            if (hasNewMessage) {
              actionsToDispatch.push(AiChatActions.selectThread({ threadId: activeThreadId }));
            }
          }

          return from(actionsToDispatch);
        }),
        mergeMap(actions => actions), // flatten the array of dispatched actions
        catchError(err => {
          console.error('Fetch Workflow Logs failed:', err);
          return EMPTY;
        })
      );
    })
  ));

}